import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import { GenericBackgroundImage } from 'takeovers/utils/components/GenericBackgroundImage'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'

// Base banner container
const Banner = styled.div`
  position: relative;
  width: 100%;
`

// Banner container for home and search pages
const HomeSearchBannerContainer = styled(Banner)`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(20)};
    justify-content: center;
    align-items: center;
    padding: ${rem(20)} ${rem(150)};
  }
`

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
`

const HomeSearchBannerContent = styled(Content)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${rem(16)};
    flex: 1 0 0;
  }
`

const MobileHomeSearchBannerContent = styled(Content)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
  flex: 1 0 0;

  ${mediaQueryUp.desktop} {
    display: none; /* Hide on desktop */
  }
`

const HomeSearchTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${rem(4)};
  flex: 1 0 0;
`

const MobileTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const MobileTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const BannerActionsGroup = styled(Content)`
  ${mediaQueryUp.desktop} {
    display: flex;
    min-width: ${rem(80)};
    padding: ${rem(10)} ${rem(12)};
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
    flex-shrink: 0; /* Prevent shrinking */
  }
`

const StyledActionButton = styled(TextButton)`
  border-radius: 2px;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.eight};
  
  &:hover {
    background: ${props => props.theme.palette.neutral.default.seven};
  }
`

const CTALinkWrapper = styled.span`
  margin-left: ${rem(8)};
`

function SiteTakeoverHomeBanner() {
  const siteTakeoverTheme = useSiteTakeoverTheme()

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  // If no theme or payload, don't render
  if (!siteTakeoverTheme || !siteTakeoverBannerContentPayload) {
    return null
  }

  // Extract values from theme and payload
  const logoRef = siteTakeoverTheme.logoRef
  const title = siteTakeoverBannerContentPayload.title
  const subtitle = siteTakeoverBannerContentPayload.subtitle
  const ctaLink = siteTakeoverBannerContentPayload.ctaLink
  const backgroundRef = siteTakeoverTheme.backgroundRef
  const backgroundStyle = siteTakeoverTheme.backgroundStyle

  // Create background element if backgroundRef exists
  const takeOverBackground = backgroundRef ? <GenericBackgroundImage imageId={backgroundRef} backgroundStyle={backgroundStyle} /> : undefined

  return (
    <HomeSearchBannerContainer>
      {takeOverBackground && <SiteTakeoverBackground>
        {takeOverBackground}
      </SiteTakeoverBackground>}
      <MobileHomeSearchBannerContent>
        <MobileTextContent>
          <MobileTitleRow>
            {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
            <Heading variant="heading4">{title}</Heading>
          </MobileTitleRow>
          <BodyText variant="medium">
            {subtitle}
            <CTALinkWrapper>
              {ctaLink && <TextLink to={ctaLink} weight="bold">View all</TextLink>}
            </CTALinkWrapper>
          </BodyText>
        </MobileTextContent>
      </MobileHomeSearchBannerContent>

      <HomeSearchBannerContent>
        {logoRef && <Image image={{ id: logoRef }} dpr={2} width={60} height={60} />}
        <HomeSearchTextWrapper>
          <Heading variant="heading3">{title}</Heading>
          <BodyText variant="large">{subtitle}</BodyText>
        </HomeSearchTextWrapper>
        {ctaLink && <BannerActionsGroup>
          <StyledActionButton kind="secondary" to={ctaLink}>
            View all offers
          </StyledActionButton>
        </BannerActionsGroup>}
      </HomeSearchBannerContent>
    </HomeSearchBannerContainer>
  )
}

export default SiteTakeoverHomeBanner
